import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const ORBIT_WASD_SPEED = 40; // m/s
/** Wheel notch ≈ this many metres along look (≈0.1 s of W/S at ORBIT_WASD_SPEED). */
const ORBIT_WHEEL_METERS_PER_LINE = 4;
const ORBIT_FREE_LOOK_SENS = 0.0022;

/** Default orbit framing used on first load and by reset. */
export const DEFAULT_ORBIT_POS = new THREE.Vector3(80, 60, 120);
export const DEFAULT_ORBIT_TARGET = new THREE.Vector3(40, 8, 15);

const _freeLookX = new THREE.Vector3(1, 0, 0);
const _freeLookY = new THREE.Vector3(0, 1, 0);

/** Deterministic hash → [0, 1) for procedural noise. */
function hash2(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Bilinear value noise. */
function valueNoise(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);
  const a = hash2(x0, y0);
  const b = hash2(x0 + 1, y0);
  const c = hash2(x0, y0 + 1);
  const d = hash2(x0 + 1, y0 + 1);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}

function fbm(x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise(x * freq, y * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

function makeGroundNoiseTexture(size = 512): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const data = img.data;
  // Base grass RGB
  const br = 125;
  const bg = 168;
  const bb = 100;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      // Multi-scale mottling so nearby and mid-range depth both read clearly
      const n =
        fbm(u * 6, v * 6, 4) * 0.55 +
        fbm(u * 22, v * 22, 3) * 0.3 +
        fbm(u * 64, v * 64, 2) * 0.15;
      const t = (n - 0.5) * 2; // ≈ [-1, 1]
      const i = (y * size + x) * 4;
      data[i] = Math.max(0, Math.min(255, br + t * 38 + (n - 0.5) * 18));
      data[i + 1] = Math.max(0, Math.min(255, bg + t * 32 - (n - 0.5) * 12));
      data[i + 2] = Math.max(0, Math.min(255, bb + t * 22 + (n - 0.5) * 10));
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Equirect-style sky with soft cloud mottling so yaw/pitch are easy to read. */
function makeSkyNoiseTexture(width = 1024, height = 512): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(width, height);
  const data = img.data;
  for (let y = 0; y < height; y++) {
    const v = y / (height - 1); // 0 = bottom (nadir), 1 = top (zenith)
    // Horizon → zenith gradient (matches former clear color ~0x9fc7e8)
    const elev = smoothstep(Math.min(1, Math.max(0, (v - 0.35) / 0.55)));
    const baseR = 175 + elev * 35; // warmer/lighter near zenith
    const baseG = 200 + elev * 28;
    const baseB = 220 + elev * 20;
    for (let x = 0; x < width; x++) {
      const u = x / width;
      // Longitude-varying clouds so rotation is obvious; fade toward horizon
      const cloud =
        fbm(u * 8 + 3.1, v * 4 + 1.7, 5) * 0.65 +
        fbm(u * 28 + 9.2, v * 14 + 4.4, 3) * 0.35;
      const mottling = (cloud - 0.48) * elev * elev;
      const i = (y * width + x) * 4;
      data[i] = Math.max(0, Math.min(255, baseR + mottling * 55));
      data[i + 1] = Math.max(0, Math.min(255, baseG + mottling * 50));
      data[i + 2] = Math.max(0, Math.min(255, baseB + mottling * 40));
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

export class SceneHost {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  /** Orbit / build / simulate camera (right pane in fly split-screen). */
  readonly camera: THREE.PerspectiveCamera;
  /** First-person fly camera (left pane in fly split-screen). */
  readonly flyCamera: THREE.PerspectiveCamera;
  readonly orbit: OrbitControls;
  readonly ground: THREE.Mesh;
  /** When true, left = flyCamera, right = orbit camera. */
  splitScreen = false;
  /** Fraction of the viewport width given to the fly (left) pane. */
  splitRatio = 0.5;
  /** WASD pans the orbit camera (disabled while fly pointer-lock owns WASD). */
  orbitWasdEnabled = true;
  /**
   * When true (default), the render loop forces camera.up to world Y so the
   * view stays upright (no roll) while free-looking / panning.
   */
  orbitKeepWorldUp = true;

  /** Craft marker shown only in the orbit pane while flying. */
  readonly flyMarker: THREE.Group;

  private grid: THREE.GridHelper | null = null;
  private gridCellSize = 5;
  private gridVisible = false;

  private wasdKeys = new Set<string>();
  private wasdForward = new THREE.Vector3();
  private wasdRight = new THREE.Vector3();
  private wasdDelta = new THREE.Vector3();
  private freeLookDeltaQ = new THREE.Quaternion();
  /** CSS-pixel drawing size — setViewport/setScissor multiply by DPR themselves. */
  private renderSize = new THREE.Vector2();

  /** Seat-relative look offsets for on-ride POV (radians). */
  private povYaw = 0;
  private povPitch = 0;
  private povLookEnabled = false;
  private povDragging = false;
  private povDragLast = { x: 0, y: 0 };
  private povPointerId: number | null = null;

  /** LMB drag: rotate the camera in place (not orbit around a point). */
  private orbitFreeLookArmed = false;
  private orbitFreeLooking = false;
  private orbitFreeLookLast = { x: 0, y: 0 };
  private orbitFreeLookDown = { x: 0, y: 0 };
  private orbitFreeLookPointerId: number | null = null;

  /** RMB drag: grab a world point and keep it under the cursor (pan). */
  private orbitGrabArmed = false;
  private orbitGrabbing = false;
  private orbitGrabDown = { x: 0, y: 0 };
  private orbitGrabPointerId: number | null = null;
  private readonly orbitGrabPoint = new THREE.Vector3();
  private readonly orbitGrabPlane = new THREE.Plane();
  private readonly orbitGrabHit = new THREE.Vector3();
  private readonly orbitGrabDelta = new THREE.Vector3();
  private readonly orbitGrabRaycaster = new THREE.Raycaster();
  private readonly orbitGrabNdc = new THREE.Vector2();
  private readonly orbitGrabGroundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.autoClear = true;

    this.scene = new THREE.Scene();
    // Fallback clear color; sky dome provides the visible sky with noise
    this.scene.background = new THREE.Color(0x9fc7e8);
    this.scene.fog = new THREE.Fog(0x9fc7e8, 300, 900);

    const skyGeo = new THREE.SphereGeometry(1800, 48, 24);
    const skyMat = new THREE.MeshBasicMaterial({
      map: makeSkyNoiseTexture(),
      side: THREE.BackSide,
      fog: false,
      depthWrite: false,
    });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    sky.name = 'sky';
    sky.renderOrder = -1;
    this.scene.add(sky);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 2000);
    this.camera.position.copy(DEFAULT_ORBIT_POS);

    this.flyCamera = new THREE.PerspectiveCamera(70, 1, 0.1, 2000);
    this.flyCamera.position.set(0, 8, 0);

    this.orbit = new OrbitControls(this.camera, canvas);
    this.orbit.target.copy(DEFAULT_ORBIT_TARGET);
    // Full polar range: LMB free-look keeps the camera fixed and only turns the
    // view; the old near-horizon clamp would shove the camera when looking up/down.
    this.orbit.minPolarAngle = 0;
    this.orbit.maxPolarAngle = Math.PI;
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.12;
    // Custom pointer handlers: LMB free-look, RMB grab-pan, wheel = W/S (not OrbitControls gestures).
    this.orbit.enableRotate = false;
    this.orbit.enablePan = false;
    this.orbit.enableZoom = false;

    const hemi = new THREE.HemisphereLight(0xdfeafa, 0x5a6b52, 0.9);
    this.scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xfff4e0, 2.0);
    sun.position.set(120, 180, 60);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -180;
    sun.shadow.camera.right = 180;
    sun.shadow.camera.top = 180;
    sun.shadow.camera.bottom = -180;
    sun.shadow.camera.far = 500;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun);

    const groundGeo = new THREE.PlaneGeometry(2000, 2000);
    const groundMap = makeGroundNoiseTexture();
    // ~20 m per tile — fine enough for near depth, coarse enough at distance
    groundMap.repeat.set(100, 100);
    groundMap.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    const groundMat = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      map: groundMap,
    });
    this.ground = new THREE.Mesh(groundGeo, groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.ground.name = 'ground';
    this.scene.add(this.ground);

    this.rebuildGrid(this.gridCellSize);
    if (this.grid) this.grid.visible = this.gridVisible;

    this.flyMarker = this.makeFlyMarker();
    this.flyMarker.visible = false;
    this.scene.add(this.flyMarker);

    window.addEventListener('keydown', (e) => this.onWasdDown(e));
    window.addEventListener('keyup', (e) => this.onWasdUp(e));

    canvas.addEventListener('pointerdown', (e) => this.onViewerPointerDown(e));
    canvas.addEventListener('pointermove', (e) => this.onViewerPointerMove(e));
    canvas.addEventListener('pointerup', (e) => this.onViewerPointerUp(e));
    canvas.addEventListener('pointercancel', (e) => this.onViewerPointerUp(e));
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('wheel', (e) => this.onViewerWheel(e), { passive: false });

    this.resize();
    window.addEventListener('resize', () => this.resize());
    // Keep camera aspect in sync when the grid layout changes without a window resize
    // (e.g. bottom graph height), otherwise picking drifts from what you see.
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.resize());
      ro.observe(canvas.parentElement ?? canvas);
    }
  }

  /**
   * Toggle the ground snap grid and (re)build it for `cellSize` metres.
   * Hidden when snap is off.
   */
  setSnapGrid(visible: boolean, cellSize: number): void {
    const cell = Math.max(0.25, cellSize);
    if (!this.grid || Math.abs(cell - this.gridCellSize) > 1e-9) {
      this.rebuildGrid(cell);
    }
    this.gridVisible = visible;
    if (this.grid) this.grid.visible = visible;
  }

  private rebuildGrid(cellSize: number): void {
    if (this.grid) {
      this.scene.remove(this.grid);
      this.grid.geometry.dispose();
      const mat = this.grid.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
      this.grid = null;
    }
    const cell = Math.max(0.25, cellSize);
    this.gridCellSize = cell;
    // Keep ~40 cells across; clamp world extent so tiny/huge cells stay usable.
    const divisions = 40;
    const size = divisions * cell;
    const grid = new THREE.GridHelper(size, divisions, 0x4a5d43, 0x64815a);
    const mats = Array.isArray(grid.material) ? grid.material : [grid.material];
    for (const m of mats) {
      m.transparent = true;
      m.opacity = 0.35;
    }
    grid.position.y = 0.02;
    grid.visible = this.gridVisible;
    this.scene.add(grid);
    this.grid = grid;
  }

  /** Enable LMB drag look-around while the seat POV drives camera position. */
  setPovLookEnabled(on: boolean): void {
    if (this.povLookEnabled === on) return;
    this.povLookEnabled = on;
    this.endViewerPointerDrag();
    this.povYaw = 0;
    this.povPitch = 0;
  }

  /**
   * Place the camera at the seat with the car's orientation, then apply any LMB
   * look offset (yaw about seat up, pitch about local right).
   */
  applyPovCamera(pos: THREE.Vector3, quaternion: THREE.Quaternion): void {
    const cam = this.camera;
    cam.position.copy(pos);
    cam.quaternion.copy(quaternion);
    if (this.povYaw !== 0) {
      this.freeLookDeltaQ.setFromAxisAngle(_freeLookY, this.povYaw);
      cam.quaternion.multiply(this.freeLookDeltaQ);
    }
    if (this.povPitch !== 0) {
      this.freeLookDeltaQ.setFromAxisAngle(_freeLookX, this.povPitch);
      cam.quaternion.multiply(this.freeLookDeltaQ);
    }
    cam.up.set(0, 1, 0).applyQuaternion(cam.quaternion).normalize();
  }

  private onViewerPointerDown(e: PointerEvent): void {
    if (this.povLookEnabled) {
      if (e.button !== 0) return;
      this.povDragging = true;
      this.povPointerId = e.pointerId;
      this.povDragLast = { x: e.clientX, y: e.clientY };
      try {
        this.renderer.domElement.setPointerCapture(e.pointerId);
      } catch {
        // capture is best-effort
      }
      return;
    }
    // Only when orbit is active (gizmo / node pick temporarily disable it).
    if (!this.orbit.enabled) return;

    if (e.button === 2) {
      this.endOrbitFreeLook();
      if (!this.pickOrbitGrabPoint(e.clientX, e.clientY, this.orbitGrabPoint)) return;
      this.orbitGrabArmed = true;
      this.orbitGrabbing = false;
      this.orbitGrabPointerId = e.pointerId;
      this.orbitGrabDown = { x: e.clientX, y: e.clientY };
      return;
    }

    if (e.button === 0) {
      this.endOrbitGrab();
      this.orbitFreeLookArmed = true;
      this.orbitFreeLooking = false;
      this.orbitFreeLookPointerId = e.pointerId;
      this.orbitFreeLookDown = { x: e.clientX, y: e.clientY };
      this.orbitFreeLookLast = { x: e.clientX, y: e.clientY };
    }
  }

  private onViewerPointerMove(e: PointerEvent): void {
    if (this.povLookEnabled) {
      if (!this.povDragging) return;
      if (this.povPointerId != null && e.pointerId !== this.povPointerId) return;
      const dx = e.clientX - this.povDragLast.x;
      const dy = e.clientY - this.povDragLast.y;
      this.povDragLast = { x: e.clientX, y: e.clientY };
      if (dx === 0 && dy === 0) return;
      this.povYaw -= dx * ORBIT_FREE_LOOK_SENS;
      // Keep pitch short of ±90° so the view doesn't flip through the seat up.
      const limit = Math.PI * 0.5 - 0.05;
      this.povPitch = Math.max(-limit, Math.min(limit, this.povPitch - dy * ORBIT_FREE_LOOK_SENS));
      return;
    }

    if (this.orbitGrabArmed || this.orbitGrabbing) {
      if (this.orbitGrabPointerId == null || e.pointerId === this.orbitGrabPointerId) {
        this.onOrbitGrabMove(e);
        return;
      }
    }

    if (!this.orbitFreeLookArmed && !this.orbitFreeLooking) return;
    if (this.orbitFreeLookPointerId != null && e.pointerId !== this.orbitFreeLookPointerId) return;
    // Gizmo / pick disabled orbit mid-gesture — abort free look.
    if (!this.orbit.enabled) {
      this.endOrbitFreeLook();
      return;
    }
    if (!this.orbitFreeLooking) {
      const moved = Math.hypot(e.clientX - this.orbitFreeLookDown.x, e.clientY - this.orbitFreeLookDown.y);
      if (moved < 4) return;
      this.orbitFreeLooking = true;
      this.orbitFreeLookLast = { x: e.clientX, y: e.clientY };
      try {
        this.renderer.domElement.setPointerCapture(e.pointerId);
      } catch {
        // capture is best-effort
      }
    }
    const dx = e.clientX - this.orbitFreeLookLast.x;
    const dy = e.clientY - this.orbitFreeLookLast.y;
    this.orbitFreeLookLast = { x: e.clientX, y: e.clientY };
    if (dx === 0 && dy === 0) return;
    this.applyOrbitFreeLook(dx, dy);
  }

  private onOrbitGrabMove(e: PointerEvent): void {
    if (this.orbitGrabPointerId != null && e.pointerId !== this.orbitGrabPointerId) return;
    if (!this.orbit.enabled) {
      this.endOrbitGrab();
      return;
    }
    if (!this.orbitGrabbing) {
      const moved = Math.hypot(e.clientX - this.orbitGrabDown.x, e.clientY - this.orbitGrabDown.y);
      if (moved < 4) return;
      this.orbitGrabbing = true;
      try {
        this.renderer.domElement.setPointerCapture(e.pointerId);
      } catch {
        // capture is best-effort
      }
    }
    this.applyOrbitGrabPan(e.clientX, e.clientY);
  }

  private onViewerPointerUp(e: PointerEvent): void {
    if (e.type === 'pointercancel') {
      this.endViewerPointerDrag();
      return;
    }
    if (this.povLookEnabled) {
      if (e.button === 0) this.endPovDrag();
      return;
    }
    if (e.button === 2) this.endOrbitGrab();
    else if (e.button === 0) this.endOrbitFreeLook();
  }

  private endViewerPointerDrag(): void {
    this.endPovDrag();
    this.endOrbitFreeLook();
    this.endOrbitGrab();
  }

  private endPovDrag(): void {
    if (this.povPointerId != null) {
      try {
        if (this.renderer.domElement.hasPointerCapture(this.povPointerId)) {
          this.renderer.domElement.releasePointerCapture(this.povPointerId);
        }
      } catch {
        // ignore
      }
    }
    this.povDragging = false;
    this.povPointerId = null;
  }

  private endOrbitFreeLook(): void {
    if (this.orbitFreeLookPointerId != null) {
      try {
        if (this.renderer.domElement.hasPointerCapture(this.orbitFreeLookPointerId)) {
          this.renderer.domElement.releasePointerCapture(this.orbitFreeLookPointerId);
        }
      } catch {
        // ignore
      }
    }
    this.orbitFreeLookArmed = false;
    this.orbitFreeLooking = false;
    this.orbitFreeLookPointerId = null;
  }

  private endOrbitGrab(): void {
    if (this.orbitGrabPointerId != null) {
      try {
        if (this.renderer.domElement.hasPointerCapture(this.orbitGrabPointerId)) {
          this.renderer.domElement.releasePointerCapture(this.orbitGrabPointerId);
        }
      } catch {
        // ignore
      }
    }
    this.orbitGrabArmed = false;
    this.orbitGrabbing = false;
    this.orbitGrabPointerId = null;
  }

  /** True for meshes we can grab to pan (track, ground, supports, …) — not gizmos/sky. */
  private isOrbitGrabPickable(obj: THREE.Object3D): boolean {
    for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
      if ((o as { isTransformControls?: boolean }).isTransformControls) return false;
      if (o === this.flyMarker || o === this.grid) return false;
      const n = o.name;
      if (n === 'sky' || n === 'overlays' || n === 'scrub') return false;
      if (
        o === this.ground ||
        n === 'track' ||
        n === 'supports' ||
        n === 'train' ||
        n === 'printPreview' ||
        n === 'chunkCube'
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * World point under the cursor for grab-pan: first hit on scene content, else
   * the ground plane, else a view-facing plane through the orbit target.
   */
  private pickOrbitGrabPoint(clientX: number, clientY: number, out: THREE.Vector3): boolean {
    const canvas = this.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;
    this.orbitGrabNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.orbitGrabNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.orbitGrabRaycaster.setFromCamera(this.orbitGrabNdc, this.camera);

    const hits = this.orbitGrabRaycaster.intersectObjects(this.scene.children, true);
    for (const hit of hits) {
      if (this.isOrbitGrabPickable(hit.object)) {
        out.copy(hit.point);
        return true;
      }
    }

    const ray = this.orbitGrabRaycaster.ray;
    if (ray.intersectPlane(this.orbitGrabGroundPlane, out)) return true;

    this.camera.getWorldDirection(this.wasdForward);
    this.orbitGrabPlane.setFromNormalAndCoplanarPoint(this.wasdForward, this.orbit.target);
    return ray.intersectPlane(this.orbitGrabPlane, out) != null;
  }

  /**
   * Translate camera + orbit target so `orbitGrabPoint` stays under the cursor
   * (drag the world).
   */
  private applyOrbitGrabPan(clientX: number, clientY: number): void {
    const canvas = this.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    this.orbitGrabNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.orbitGrabNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.orbitGrabRaycaster.setFromCamera(this.orbitGrabNdc, this.camera);

    this.camera.getWorldDirection(this.wasdForward);
    this.orbitGrabPlane.setFromNormalAndCoplanarPoint(this.wasdForward, this.orbitGrabPoint);
    if (this.orbitGrabRaycaster.ray.intersectPlane(this.orbitGrabPlane, this.orbitGrabHit) == null) return;

    this.orbitGrabDelta.copy(this.orbitGrabPoint).sub(this.orbitGrabHit);
    if (this.orbitGrabDelta.lengthSq() < 1e-16) return;
    this.camera.position.add(this.orbitGrabDelta);
    this.orbit.target.add(this.orbitGrabDelta);
  }

  private makeFlyMarker(): THREE.Group {
    const g = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(0.6, 2.2, 6),
      new THREE.MeshBasicMaterial({ color: 0xffcc33 }),
    );
    body.rotation.x = -Math.PI / 2; // tip along local −Z (camera look)
    g.add(body);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.9, 1.15, 24),
      new THREE.MeshBasicMaterial({ color: 0xffee88, side: THREE.DoubleSide, depthTest: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    g.add(ring);
    return g;
  }

  private onWasdDown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const k = e.key.toLowerCase();
    if (k === 'w' || k === 'a' || k === 's' || k === 'd') this.wasdKeys.add(k);
    if (e.key === 'Shift') this.wasdKeys.add('shift');
    if (e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      this.wasdKeys.add('space');
    }
  }

  private onWasdUp(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'w' || k === 'a' || k === 's' || k === 'd') this.wasdKeys.delete(k);
    if (e.key === 'Shift') this.wasdKeys.delete('shift');
    if (e.code === 'Space' || e.key === ' ') this.wasdKeys.delete('space');
  }

  /**
   * Pan the orbit camera with WASD (camera + target together). Space = up,
   * Shift = down — including while fly-mode has the left pane locked.
   */
  updateOrbitWasd(dt: number): void {
    const horiz = this.orbitWasdEnabled && this.orbit.enabled;
    const vertUp = this.wasdKeys.has('space');
    const vertDown = this.wasdKeys.has('shift');
    if (!horiz && !vertUp && !vertDown) return;

    this.wasdDelta.set(0, 0, 0);
    if (horiz) {
      this.camera.getWorldDirection(this.wasdForward);
      this.wasdRight.crossVectors(this.wasdForward, this.camera.up).normalize();
      if (this.wasdRight.lengthSq() < 1e-6) this.wasdRight.set(1, 0, 0);
      if (this.wasdKeys.has('w')) this.wasdDelta.add(this.wasdForward);
      if (this.wasdKeys.has('s')) this.wasdDelta.sub(this.wasdForward);
      if (this.wasdKeys.has('d')) this.wasdDelta.add(this.wasdRight);
      if (this.wasdKeys.has('a')) this.wasdDelta.sub(this.wasdRight);
    }
    if (vertUp) this.wasdDelta.y += 1;
    if (vertDown) this.wasdDelta.y -= 1;
    if (this.wasdDelta.lengthSq() < 1e-8) return;

    this.wasdDelta.normalize().multiplyScalar(ORBIT_WASD_SPEED * dt);
    this.camera.position.add(this.wasdDelta);
    this.orbit.target.add(this.wasdDelta);
  }

  /**
   * Mouse wheel = W/S: move camera + target along the look direction.
   * Scroll up → forward (W), scroll down → back (S).
   */
  private onViewerWheel(e: WheelEvent): void {
    if (this.povLookEnabled) return;
    if (!this.orbit.enabled || !this.orbitWasdEnabled) return;
    if (e.deltaY === 0) return;

    e.preventDefault();

    let lines = e.deltaY;
    if (e.deltaMode === WheelEvent.DOM_DELTA_PIXEL) lines /= 100;
    else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) lines *= 16;

    // negative deltaY (scroll up) → forward, same as W
    const dist = -lines * ORBIT_WHEEL_METERS_PER_LINE;
    if (Math.abs(dist) < 1e-8) return;

    this.camera.getWorldDirection(this.wasdForward);
    this.wasdDelta.copy(this.wasdForward).multiplyScalar(dist);
    this.camera.position.add(this.wasdDelta);
    this.orbit.target.add(this.wasdDelta);
  }

  /** Move orbit target (and camera by the same delta) toward `tip`, easing with `rate`. */
  panOrbitToward(tip: THREE.Vector3, dt: number, rate = 4): void {
    const alpha = 1 - Math.exp(-rate * dt);
    this.wasdDelta.copy(tip).sub(this.orbit.target).multiplyScalar(alpha);
    if (this.wasdDelta.lengthSq() < 1e-10) return;
    this.orbit.target.add(this.wasdDelta);
    this.camera.position.add(this.wasdDelta);
  }

  /**
   * Ease the orbit camera to a zoomed-out overview of `center` with world-space
   * half-extent `radius`, keeping the current viewing angle.
   */
  frameOrbitOverview(center: THREE.Vector3, radius: number, dt: number, rate = 2.5): void {
    const pad = Math.max(radius, 20);
    const fov = THREE.MathUtils.degToRad(this.camera.fov);
    const halfH = Math.tan(fov * 0.5);
    const halfW = halfH * Math.max(this.camera.aspect, 0.2);
    // distance so a sphere of `pad` fits in both axes, plus margin
    const dist = (pad / Math.min(halfH, halfW)) * 1.45;

    this.wasdForward.copy(this.camera.position).sub(this.orbit.target);
    if (this.wasdForward.lengthSq() < 1e-6) this.wasdForward.set(1, 0.7, 1);
    this.wasdForward.normalize().multiplyScalar(dist);

    const alpha = 1 - Math.exp(-rate * dt);
    // target → center
    this.wasdDelta.copy(center).sub(this.orbit.target).multiplyScalar(alpha);
    this.orbit.target.add(this.wasdDelta);
    // camera → center + offset (zoom out / in with the bounds)
    this.wasdRight.copy(center).add(this.wasdForward); // desired cam pos
    this.wasdDelta.copy(this.wasdRight).sub(this.camera.position).multiplyScalar(alpha);
    this.camera.position.add(this.wasdDelta);
  }

  /**
   * Keep the orbit camera position, but aim it at `tip` (rotate view to the
   * latest track piece without panning/zooming).
   */
  lookOrbitAt(tip: THREE.Vector3): void {
    this.orbit.target.copy(tip);
    this.camera.up.set(0, 1, 0);
    this.orbit.update();
  }

  /** Restore the factory orbit position, look-at target, and up vector. */
  resetOrbitCamera(): void {
    this.camera.position.copy(DEFAULT_ORBIT_POS);
    this.orbit.target.copy(DEFAULT_ORBIT_TARGET);
    this.camera.up.set(0, 1, 0);
    this.orbit.update();
  }

  /**
   * Rotate the camera in place (pitch/yaw). Camera position stays put; the look
   * target is moved to stay in front at the current distance so pan/zoom remain coherent.
   */
  applyOrbitFreeLook(dx: number, dy: number, sens = ORBIT_FREE_LOOK_SENS): void {
    const cam = this.camera;
    this.freeLookDeltaQ.setFromAxisAngle(_freeLookX, -dy * sens);
    cam.quaternion.multiply(this.freeLookDeltaQ);
    this.freeLookDeltaQ.setFromAxisAngle(_freeLookY, -dx * sens);
    cam.quaternion.multiply(this.freeLookDeltaQ);
    cam.up.set(0, 1, 0).applyQuaternion(cam.quaternion).normalize();

    const dist = Math.max(cam.position.distanceTo(this.orbit.target), 1);
    cam.getWorldDirection(this.wasdForward);
    this.orbit.target.copy(cam.position).addScaledVector(this.wasdForward, dist);
    if (this.orbitKeepWorldUp) {
      cam.up.set(0, 1, 0);
      this.orbit.update();
    }
  }

  /** Force world-up framing while keeping the current look-at target. */
  normalizeOrbitUp(): void {
    this.camera.up.set(0, 1, 0);
    this.orbit.update();
  }

  /** Sync the orbit-pane craft marker to the fly camera pose. */
  syncFlyMarker(): void {
    if (!this.splitScreen) {
      this.flyMarker.visible = false;
      return;
    }
    this.flyMarker.visible = true;
    this.flyMarker.position.copy(this.flyCamera.position);
    this.flyMarker.quaternion.copy(this.flyCamera.quaternion);
  }

  setSplitScreen(on: boolean): void {
    if (this.splitScreen === on) return;
    this.splitScreen = on;
    this.flyMarker.visible = on;
    this.resize();
  }

  /** Left-pane width fraction in split-screen (clamped). */
  setSplitRatio(ratio: number): void {
    const next = Math.min(0.8, Math.max(0.2, ratio));
    if (Math.abs(next - this.splitRatio) < 1e-4) return;
    this.splitRatio = next;
    if (this.splitScreen) this.resize();
  }

  /** Pixel width of the left (fly) pane for a given buffer/CSS width. */
  leftPaneWidth(total: number): number {
    return Math.max(1, Math.floor(total * this.splitRatio));
  }

  resize(): void {
    const canvas = this.renderer.domElement;
    const w = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    if (this.splitScreen) {
      const left = this.leftPaneWidth(w);
      const right = Math.max(w - left, 1);
      this.flyCamera.aspect = left / h;
      this.camera.aspect = right / h;
    } else {
      this.camera.aspect = w / h;
    }
    this.flyCamera.updateProjectionMatrix();
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    // IMPORTANT: setViewport/setScissor take CSS pixels and multiply by pixel
    // ratio internally. Passing canvas.width (already DPR-scaled) made the GL
    // viewport larger than the buffer — the view looked shifted top-right and
    // picking/orbit felt offset (common on Windows display scaling).
    this.renderer.getSize(this.renderSize);
    const w = Math.max(1, this.renderSize.x);
    const h = Math.max(1, this.renderSize.y);

    if (!this.splitScreen) {
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
      this.flyMarker.visible = false;
      this.renderer.render(this.scene, this.camera);
      return;
    }

    const left = this.leftPaneWidth(w);
    const right = Math.max(w - left, 1);
    this.renderer.setScissorTest(true);

    // left — fly POV (hide craft marker so it doesn't sit in your face)
    this.flyMarker.visible = false;
    this.renderer.setViewport(0, 0, left, h);
    this.renderer.setScissor(0, 0, left, h);
    this.renderer.render(this.scene, this.flyCamera);

    // right — orbit overview
    this.flyMarker.visible = true;
    this.renderer.setViewport(left, 0, right, h);
    this.renderer.setScissor(left, 0, right, h);
    this.renderer.render(this.scene, this.camera);
  }
}
